/**
 * Public Tesseract auth staff roster (Minecraft UUIDs + team labels).
 * Used to scope staff pickers to known staff instead of global player search.
 */

export interface TesseractStaffEntry {
  minecraft_uuid: string;
  teams: string[];
}

interface StaffApiResponse {
  success: boolean;
  data?: {
    staff: TesseractStaffEntry[];
    cached_at?: number;
  };
}

const DEFAULT_TESSERACT_AUTH_BASE = 'https://craftdownunder.co';

function staffListUrl(): string {
  const fromEnv = import.meta.env.VITE_TESSERACT_AUTH_URL as string | undefined;
  if (fromEnv && fromEnv.trim()) {
    return `${fromEnv.replace(/\/$/, '')}/auth/public/staff`;
  }
  if (import.meta.env.DEV) {
    return '/tesseract-auth/auth/public/staff';
  }
  return `${DEFAULT_TESSERACT_AUTH_BASE}/auth/public/staff`;
}

let cachedStaff: TesseractStaffEntry[] | null = null;
let cacheFetchedAtMs: number | null = null;
let inflight: Promise<TesseractStaffEntry[]> | null = null;

/** Client-side freshness; API `cached_at` is informational only. */
const CLIENT_CACHE_TTL_MS = 5 * 60 * 1000;

export function invalidateTesseractStaffCache(): void {
  cachedStaff = null;
  cacheFetchedAtMs = null;
}

export async function fetchTesseractStaffList(): Promise<TesseractStaffEntry[]> {
  const now = Date.now();
  if (
    cachedStaff &&
    cacheFetchedAtMs != null &&
    now - cacheFetchedAtMs < CLIENT_CACHE_TTL_MS
  ) {
    return cachedStaff;
  }
  if (inflight) return inflight;

  inflight = (async () => {
    const res = await fetch(staffListUrl(), { credentials: 'omit' });
    if (!res.ok) {
      throw new Error(`Staff list request failed (${res.status})`);
    }
    const body = (await res.json()) as StaffApiResponse;
    if (!body.success || !body.data?.staff || !Array.isArray(body.data.staff)) {
      throw new Error('Invalid staff list response');
    }
    cachedStaff = body.data.staff;
    cacheFetchedAtMs = Date.now();
    return cachedStaff;
  })();

  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}
